import { FC, KeyboardEvent, useCallback } from 'react'
import { SaturatedItem, stencil } from 'react-common'
import { Link } from 'react-router-dom'
import { isExternalPath, isUrl } from '@/features/layout/utils/isUrl'
import { removeRootPath } from '@/features/layout/utils/removeRootPath'
import { CustomTreeNode } from '@/store/slices/layoutSlice'
import './SideBarItem.scss'
import { isValidSideBarItem } from './utils'

const { SideBarItemComponent } = stencil

type SideBarItemProps = {
  item: CustomTreeNode
  activeSlug: string | null
}

export const SideBarItem: FC<SideBarItemProps> = ({ item, activeSlug }) => {
  const handleKeyDown = useCallback((evt: KeyboardEvent<HTMLAnchorElement>) => {
    if (evt.code !== 'Enter') return
    evt.currentTarget.click()
  }, [])

  if (!isValidSideBarItem(item)) {
    return null
  }

  const child = (
    <SideBarItemComponent
      key={item.name}
      title={item.name}
      iconId={item.icon_id}
      active={activeSlug === item.slug}
      onClick={item.onClick}
      badge={item.badge}
      {...(item.children ? { variant: 'dropdown', items: item.children as SaturatedItem[] } : { variant: 'default' })}
    />
  )

  if (!item.url) {
    return child
  }

  if (isUrl(item.url)) {
    return (
      <a
        key={item.name}
        href={removeRootPath(item.url)}
        target="_blank"
        rel="noreferrer"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        {child}
      </a>
    )
  }

  if (isExternalPath(item.url)) {
    return (
      <a key={item.name} href={item.url} tabIndex={-1} onKeyDown={handleKeyDown}>
        {child}
      </a>
    )
  }

  return (
    <Link key={item.name} to={removeRootPath(item.url)} tabIndex={-1} onKeyDown={handleKeyDown}>
      {child}
    </Link>
  )
}
